import { useAuth } from "@/context/auth-context";
import { action, computed, makeAutoObservable, observable } from "mobx";
import { useLocalObservable } from "mobx-react-lite";
import { createContext, useContext, useEffect, useState } from "react";
import { Model } from "@/queries/models";
import { useModels } from "@/queries/models";
import { observer } from "mobx-react-lite";
import { LLMProviderContext } from "./provider-store";

export class ModelsStore {
  @observable _models: Model[] = [];
  @observable _modelsLoading: boolean = false;
  @observable _modelsPage: number = 1;
  @observable _modelsPageSize: number = 250;
  @observable _selectedProviderId: number | undefined = undefined;
  @observable _isEnabled: boolean = false;
  @observable _search: string = "";
  @observable _allowEmptySearch: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  @action setModels = (models: Model[]) => {
    this._models = models;
  };

  @action setModelsLoading = (modelsLoading: boolean) => {
    this._modelsLoading = modelsLoading;
  };

  @action setModelsPageSize = (modelsPageSize: number) => {
    this._modelsPageSize = modelsPageSize;
  };

  @action setModelsPage = (modelsPage: number) => {
    this._modelsPage = modelsPage;
  };

  @action setSelectedProviderId = (providerId: number | undefined) => {
    if (this._selectedProviderId !== providerId) {
      this._selectedProviderId = providerId;
      this._models = [];
    }
  };

  @computed get models() {
    return this._models;
  }

  @computed get modelsLoading() {
    return this._modelsLoading;
  }

  @computed get modelsPage() {
    return this._modelsPage;
  }

  @computed get modelsPageSize() {
    return this._modelsPageSize;
  }

  @computed get selectedProviderId() {
    return this._selectedProviderId;
  }

  @computed get isEnabled() {
    return this._isEnabled;
  }

  @computed get search() {
    return this._search;
  }

  @computed get allowEmptySearch() {
    return this._allowEmptySearch;
  }

  @action setSearch = (search: string) => {
    this._search = search;
  };

  @action setAllowEmptySearch = (allowEmptySearch: boolean) => {
    this._allowEmptySearch = allowEmptySearch;
  };

  @action reset = () => {
    this._models = [];
    this._modelsLoading = false;
    this._modelsPage = 1;
    this._selectedProviderId = undefined;
  };
}

export interface ModelsContextType {
  store: ModelsStore;
  setModelsPage: React.Dispatch<React.SetStateAction<number>>;
  isEnabled: boolean;
  setIsEnabled: React.Dispatch<React.SetStateAction<boolean>>;
}

const ModelsContext = createContext<ModelsContextType | undefined>(undefined);

export const ModelsStoreProvider = observer(
  ({ children }: { children: React.ReactNode }) => {
    const [isEnabled, setIsEnabled] = useState(false);
    const providerContext = useContext(LLMProviderContext);
    const { providers } = providerContext?.store || {};
    const store = useLocalObservable(() => new ModelsStore());
    const auth = useAuth();
    const userToken = auth!.userToken;
    const isCustomProvider = Boolean(
      providers?.find((provider) => provider.id === store.selectedProviderId)
        ?.workspace_id,
    );

    const {
      data: models,
      isLoading: modelsLoading,
      isRefetching: modelsRefetching,
      setPage: setModelsPage,
    } = useModels({
      userToken: userToken!,
      providerId:
        store.search && isCustomProvider ? undefined : store.selectedProviderId,
      enabled: !!userToken && !!store.selectedProviderId && isEnabled,
      q: store.search,
      allowEmptySearch: store.allowEmptySearch,
    });

    useEffect(() => {
      if (models?.model_configs && !modelsLoading && !modelsRefetching) {
        store.setModels(models.model_configs);
      }
    }, [models?.model_configs, modelsLoading, modelsRefetching, store]);

    useEffect(() => {
      store.setModelsLoading(modelsLoading || modelsRefetching);
    }, [modelsLoading, modelsRefetching, store]);

    return (
      <ModelsContext.Provider
        value={{
          store,
          setModelsPage,
          isEnabled,
          setIsEnabled,
        }}
      >
        {children}
      </ModelsContext.Provider>
    );
  },
);

export const useModelsStore = ({
  isEnabledByDefault = false,
  allowEmptySearch = false,
}: {
  isEnabledByDefault?: boolean;
  allowEmptySearch?: boolean;
}): ModelsContextType => {
  const context = useContext(ModelsContext);

  if (!context) {
    throw new Error("useModelsStore must be used within a ModelsStoreProvider");
  }

  if (allowEmptySearch && !context.store.allowEmptySearch) {
    context?.store.setAllowEmptySearch(true);
  }

  if (isEnabledByDefault && !context.isEnabled) {
    context?.setIsEnabled(true);
  }

  return context;
};
